import { Device, Endpoint } from "fhir/r5";

/** Official R5-to-R4/R4B cross-version extension canonicals. */
export const R5_CROSS_VERSION = {
  deviceDisplayName: "http://hl7.org/fhir/5.0/StructureDefinition/extension-Device.displayName",
  deviceEndpoint: "http://hl7.org/fhir/5.0/StructureDefinition/extension-Device.endpoint",
  endpointConnectionType: "http://hl7.org/fhir/5.0/StructureDefinition/extension-Endpoint.connectionType",
  endpointEnvironmentType: "http://hl7.org/fhir/5.0/StructureDefinition/extension-Endpoint.environmentType",
} as const;

type JsonObject = Record<string, any>;

function extensions(element: unknown): JsonObject[] {
  return Array.isArray((element as JsonObject | undefined)?.extension)
    ? (element as JsonObject).extension
    : [];
}

function extensionValue(element: unknown, url: string, valueName: string): any | undefined {
  return extensions(element).find(extension => extension.url === url)?.[valueName];
}

function asArray<T>(value: T | T[] | undefined): T[] {
  return value === undefined ? [] : Array.isArray(value) ? value : [value];
}

export function deviceDisplayName(device: Device | JsonObject): string {
  const rawDevice = device as JsonObject;
  const r5Name = rawDevice.displayName;
  if (r5Name) return r5Name;
  const r4DeviceName = asArray<JsonObject>(rawDevice.deviceName)[0];
  const legacyName = rawDevice.name;
  return extensionValue(r4DeviceName, R5_CROSS_VERSION.deviceDisplayName, "valueString")
    ?? r4DeviceName?.name
    ?? r4DeviceName?.displayName
    ?? (typeof legacyName === "string" ? legacyName : undefined)
    ?? asArray<JsonObject>(legacyName)[0]?.value
    ?? rawDevice.identifier?.[0]?.value
    ?? "";
}

export function deviceTypes(device: Device | JsonObject): JsonObject[] {
  return asArray<JsonObject>((device as JsonObject).type);
}

export function deviceVersions(device: Device | JsonObject): JsonObject[] {
  return asArray<JsonObject>((device as JsonObject).version);
}

export function deviceEndpoints(device: Device | JsonObject): JsonObject[] {
  const r5Endpoints = asArray<JsonObject>((device as JsonObject).endpoint);
  return r5Endpoints.length > 0
    ? r5Endpoints
    : extensions(device)
      .filter(extension => extension.url === R5_CROSS_VERSION.deviceEndpoint)
      .map(extension => extension.valueReference)
      .filter(Boolean);
}

export function endpointConnectionTypes(endpoint: Endpoint | JsonObject): JsonObject[] {
  const r5ConnectionTypes = asArray<JsonObject>((endpoint as JsonObject).connectionType);
  if (r5ConnectionTypes.length > 0 && r5ConnectionTypes[0]?.coding) return r5ConnectionTypes;
  const r4Coding = r5ConnectionTypes[0];
  return extensionValue(r4Coding, R5_CROSS_VERSION.endpointConnectionType, "valueCodeableConcept")
    ? [extensionValue(r4Coding, R5_CROSS_VERSION.endpointConnectionType, "valueCodeableConcept")]
    : r4Coding ? [{ coding: [r4Coding] }] : [];
}

export function endpointEnvironmentTypes(endpoint: Endpoint | JsonObject): JsonObject[] {
  const r5EnvironmentTypes = asArray<JsonObject>((endpoint as JsonObject).environmentType);
  return r5EnvironmentTypes.length > 0
    ? r5EnvironmentTypes
    : extensions(endpoint)
      .filter(extension => extension.url === R5_CROSS_VERSION.endpointEnvironmentType)
      .map(extension => extension.valueCodeableConcept)
      .filter(Boolean);
}

/** Convert Pandora's R5 Device editor representation into R4 + official R5 extensions. */
export function deviceForR4Server(device: Device): Device {
  const r5 = device as unknown as JsonObject;
  const displayName = deviceDisplayName(r5);
  const r4: JsonObject = { ...r5 };
  delete r4.displayName;
  delete r4.name;
  delete r4.endpoint;
  r4.deviceName = [{
    name: displayName,
    type: "user-friendly-name",
    extension: [{ url: R5_CROSS_VERSION.deviceDisplayName, valueString: displayName }],
  }];
  r4.type = deviceTypes(r5)[0];
  r4.extension = [
    ...extensions(r4),
    ...deviceEndpoints(r5).map(valueReference => ({
      url: R5_CROSS_VERSION.deviceEndpoint,
      valueReference,
    })),
  ];
  return r4 as Device;
}

/** Convert Pandora's R5 Endpoint editor representation into R4 + official R5 extensions. */
export function endpointForR4Server(endpoint: Endpoint): Endpoint {
  const r5 = endpoint as unknown as JsonObject;
  const connectionType = endpointConnectionTypes(r5)[0];
  const environmentType = endpointEnvironmentTypes(r5)[0];
  const r4: JsonObject = { ...r5 };
  delete r4.environmentType;
  r4.connectionType = connectionType?.coding?.[0];
  r4.payloadType = r4.payloadType?.length ? r4.payloadType : [{ text: "FHIR R4" }];
  if (r4.connectionType && connectionType) {
    r4.connectionType.extension = [{
      url: R5_CROSS_VERSION.endpointConnectionType,
      valueCodeableConcept: connectionType,
    }];
  }
  r4.extension = [
    ...extensions(r4),
    ...(environmentType ? [{
      url: R5_CROSS_VERSION.endpointEnvironmentType,
      valueCodeableConcept: environmentType,
    }] : []),
  ];
  return r4 as Endpoint;
}
